import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  Customer,
  FetchClientSearchRequest,
  FetchClientSearchResponseData,
} from '../model/customer.model'

interface InitialState {
  customer: Customer
  clientExists: {
    loading: boolean | null
    errorCode: string
    errorMessage: string
    data: boolean | null
  }
  clientSearch: {
    httpStatus: number
    errorCode: string
    errorMessage: string
    data: FetchClientSearchResponseData | null
  }
}

interface Error {
  errorMessage: string
  errorCode: string
}

interface FetchClientExistsPayload {
  value: string
  tenant: string
  validationType: string
}

interface FetchClientSearchPayload {
  body: FetchClientSearchRequest
  tenant: string
}

interface FetchClientSearchResponse {
  errorMessage?: string
  errorCode?: string
  data?: FetchClientSearchResponseData | null
  httpStatus?: number
}

const customer: Customer = {
  name: '',
  firstName: '',
  lastName: '',
  docType: '',
  docId: '',
  nationality: '',
  contactPhone: '',
  companyName: '',
  cif: '',
  customerSegment: '',
  birthdate: null,
  email: '',
  language: '',
  technology: '',
  province: null,
  gender: '',
}

export const initialState: InitialState = {
  customer,
  clientExists: {
    data: null,
    loading: null,
    errorCode: '',
    errorMessage: '',
  },
  clientSearch: {
    data: null,
    httpStatus: 0,
    errorCode: '',
    errorMessage: '',
  },
}

export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setFields(state, action: PayloadAction<Partial<Customer>>) {
      return { ...state, customer: { ...state.customer, ...action.payload } }
    },
    resetCustomer() {
      return initialState
    },
    fetchClientExists(state, _action: PayloadAction<FetchClientExistsPayload>) {
      return {
        ...state,
        clientExists: {
          ...initialState.clientExists,
          loading: true,
        },
      }
    },
    fetchClientExistsSuccess(state, action: PayloadAction<boolean>) {
      const { payload } = action
      return {
        ...state,
        clientExists: {
          ...initialState.clientExists,
          loading: false,
          data: payload,
        },
      }
    },
    fetchClientExistsError(state, action: PayloadAction<Error>) {
      const {
        payload: { errorCode, errorMessage },
      } = action
      return {
        ...state,
        clientExists: {
          ...initialState.clientExists,
          loading: false,
          errorCode,
          errorMessage,
        },
      }
    },
    fetchClientSearch(state, _action: PayloadAction<FetchClientSearchPayload>) {
      return {
        ...state,
        clientSearch: {
          ...initialState.clientSearch,
        },
      }
    },
    fetchClientSearchSuccess(state, action: PayloadAction<FetchClientSearchResponse>) {
      return {
        ...state,
        clientSearch: {
          ...initialState.clientSearch,
          ...action.payload,
        },
      }
    },
    fetchClientSearchError(state, action: PayloadAction<FetchClientSearchResponse>) {
      return {
        ...state,
        clientSearch: {
          ...initialState.clientSearch,
          ...action.payload,
        },
      }
    },
  },
})

export const {
  setFields,
  resetCustomer,
  fetchClientExists,
  fetchClientExistsError,
  fetchClientExistsSuccess,
  fetchClientSearch,
  fetchClientSearchError,
  fetchClientSearchSuccess,
} = customerSlice.actions

const customerReducer = customerSlice.reducer
export default customerReducer
